"use client";

/**
 * ProposeSkillDisplay — the read-only DRAFT card the agent renders during the
 * Create a Skill flow (tool: propose_skill).
 *
 * HOW IT WAITS (mirrors ask_user):
 * The backend `_propose_skill` validates the draft, then BLOCKS on Redis until
 * the user acts — exactly like `ask_user` blocks on a question. So:
 *   - The interactive card (Explanation | Raw .md tabs + Save + Suggest a
 *     change) is shown WHILE the tool is `loading` (the agent is waiting).
 *   - Tick (Save) → POST /alerts/skills, then release the agent with
 *     `__saved__` so it confirms in one sentence and stops.
 *   - Suggest a change → release the agent with the plain-English feedback;
 *     it re-drafts and renders a fresh card (new tool call).
 * The agent NEVER barges ahead and says "it's ready" — it pauses on the card.
 *
 * The card renders straight from `draft` every render (prefer the
 * backend-validated `result.draft`, fall back to the streamed call `args`),
 * so the fields always reflect the latest data — no stale useState copy (the
 * old "blank card" bug). The backend now whitelists `draft` for streaming, so
 * `result.draft` actually arrives.
 *
 * Style: near-black surface, emerald accent, white text (NO gray).
 */

import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import {
  Hammer,
  Loader2,
  Check,
  Database,
  ArrowUpRight,
  Lock,
  PencilLine,
  Send,
  X,
  ChevronDown,
} from "lucide-react";
import { apiClient, type SkillInput } from "@/lib/api-client";
import { useAuth } from "@/contexts/AuthContext";
import { usePersonalSkills } from "@/contexts/PersonalSkillsContext";
import { prettySource } from "@/lib/skills-map";
import {
  MD_COMPONENTS,
  DESCRIPTION_MD_COMPONENTS,
} from "@/components/skills/skill-markdown";
import { cn } from "@/lib/utils";

interface SkillDraft {
  name?: string;
  description?: string;
  explanation?: string;
  agent_instruction?: string;
  data_sources?: string[];
  inputs?: SkillInput[];
  output_type?: string;
}

interface ProposeSkillDisplayProps {
  sessionId?: string;
  args?: any;
  result?: any;
  status: "loading" | "success";
  toolCallId?: string;
  isReadonly?: boolean;
  viewMode?: string;
}

const SAVED_KEY = (id: string) => `xynth:skill-saved:${id}`;
// Once the user acted on the modal (saved / dismissed / sent feedback) we never
// auto-reopen it for the same draft, across re-renders AND reloads.
const MODAL_DONE_KEY = (id: string) => `xynth:skill-modal-done:${id}`;
type BodyView = "explanation" | "raw";

const modalDone = (id?: string): boolean => {
  if (!id || typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(MODAL_DONE_KEY(id)) === "1";
  } catch {
    return false;
  }
};

const markModalDone = (id?: string) => {
  if (!id || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(MODAL_DONE_KEY(id), "1");
  } catch {
    // ignore quota
  }
};

export function ProposeSkillDisplay({
  sessionId,
  args,
  result,
  status,
  toolCallId,
  isReadonly = false,
}: ProposeSkillDisplayProps) {
  const { user } = useAuth();
  const { refresh, addCreated } = usePersonalSkills();

  // Prefer the backend-validated/normalized draft; fall back to the raw args
  // the agent streamed (so the card renders WHILE the agent is waiting, before
  // any result lands — the interactive state).
  const draft: SkillDraft = useMemo(
    () => (result?.draft as SkillDraft) || (args as SkillDraft) || {},
    [result, args],
  );

  // Coerce every field defensively. While the draft STREAMS in token-by-token,
  // a field can briefly be a half-arrived fragment (e.g. data_sources as a
  // partial string, not an array yet). Guarding here means a partial draft
  // shows the "Drafting..." loader instead of crashing on `.map`/`.trim`.
  const asText = (v: unknown): string =>
    typeof v === "string" ? v.trim() : "";
  const name = asText(draft.name);
  const description = asText(draft.description);
  const explanation = asText(draft.explanation);
  const instruction = asText(draft.agent_instruction);
  const dataSources: string[] = Array.isArray(draft.data_sources)
    ? draft.data_sources.filter((d): d is string => typeof d === "string")
    : [];
  const inputs: SkillInput[] = Array.isArray(draft.inputs) ? draft.inputs : [];
  const outputType =
    typeof draft.output_type === "string" ? draft.output_type : "research";

  const [view, setView] = useState<BodyView>("explanation");
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [changeOpen, setChangeOpen] = useState(false);
  const [changeText, setChangeText] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  // Review modal: pops open the moment the agent starts waiting on this draft,
  // once per toolCallId. Closing it without saving releases the agent with
  // __dismissed__ so the turn resolves instantly instead of timing out at 5min.
  const [modalOpen, setModalOpen] = useState(false);
  const [modalHandled, setModalHandled] = useState<boolean>(() =>
    modalDone(toolCallId),
  );
  // Saved-state preview: collapsed (short peek + fade) by default, click to grow.
  const [previewExpanded, setPreviewExpanded] = useState(false);

  // Restore a prior save (survives reload) so we never offer Save twice for the
  // same proposed draft.
  const [savedId, setSavedId] = useState<string | null>(() => {
    if (!toolCallId || typeof window === "undefined") return null;
    try {
      return window.localStorage.getItem(SAVED_KEY(toolCallId));
    } catch {
      return null;
    }
  });

  // A complete draft has everything the backend needs. Until then (mid-stream)
  // we show the drafting loader instead of a half-empty card.
  const ready = Boolean(
    name.length >= 3 &&
      explanation.length >= 40 &&
      instruction.length >= 60 &&
      dataSources.length > 0,
  );
  // The agent is actively waiting on the user when the tool is still "loading"
  // and the draft is fully streamed. This is the INTERACTIVE state (Save /
  // Suggest a change live here, same as ask_user's question lives in loading).
  const waiting = status === "loading" && ready;
  const loadingDraft = status === "loading" && !ready;

  const rstatus: string | undefined = result?.status;

  // Auto-open the review modal exactly once per draft, only while the agent is
  // actively waiting. Never in readonly/replay, never after a save.
  useEffect(() => {
    if (waiting && !modalHandled && !isReadonly && !savedId) {
      setModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting, modalHandled, isReadonly, savedId]);

  /** User closed the modal without acting: resolve the agent right away. */
  const dismissModal = () => {
    markModalDone(toolCallId);
    setModalHandled(true);
    setModalOpen(false);
    if (waiting) {
      // eslint-disable-next-line no-console
      console.log("[SKILL-CREATE] modal dismissed -> release __dismissed__", {
        toolCallId,
      });
      releaseAgent("__dismissed__");
    }
  };
  // A rejected draft (validation failed BEFORE the wait). The agent re-drafts
  // and re-calls propose_skill, so render nothing — the user only sees valid
  // cards. (A rejected draft never enters the waiting/blocking phase.)
  const rejected = status === "success" && rstatus === "error";
  const changesAck = status === "success" && rstatus === "changes_requested";

  // One log per draft landing — confirms the real values reached the card.
  useEffect(() => {
    if (status !== "success") return;
    // eslint-disable-next-line no-console
    console.log("[SKILL-CREATE] result", {
      rstatus,
      hasResultDraft: Boolean(result?.draft),
      name,
      explanationLen: explanation.length,
      instructionLen: instruction.length,
      sources: dataSources.length,
      toolCallId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, rstatus, name]);

  /** Release the waiting agent (best-effort — Save works regardless). */
  const releaseAgent = (value: string) => {
    if (!sessionId || !toolCallId) return;
    apiClient.submitProposeSkillReply(sessionId, toolCallId, value).catch((e) => {
      // eslint-disable-next-line no-console
      console.log("[SKILL-CREATE] release agent failed (non-fatal)", {
        value: value.slice(0, 40),
        msg: e?.message,
      });
    });
  };

  const handleSave = async () => {
    if (saving || savedId || !ready) return;
    setErrorMsg(null);
    setSaving(true);
    try {
      // eslint-disable-next-line no-console
      console.log("[SKILL-CREATE] save click", {
        name,
        sources: dataSources.length,
        inputs: inputs.length,
        toolCallId,
      });
      const res = await apiClient.createSkill({
        name,
        description,
        explanation,
        agent_instruction: instruction,
        data_sources: dataSources,
        inputs,
        output_type: outputType,
        source: "create_a_skill",
      });
      const newId = res?.skill?.id;
      // eslint-disable-next-line no-console
      console.log("[SKILL-CREATE] save OK", { newId, created: res?.created });
      if (newId && toolCallId) {
        try {
          window.localStorage.setItem(SAVED_KEY(toolCallId), newId);
        } catch {
          // ignore quota
        }
      }
      setSavedId(newId || "saved");
      // Optimistic: drop the new skill into the shared store NOW so it pops into
      // the sidebar + chat-bar modal instantly (the sidebar shakes it). refresh()
      // then reconciles against the backend (same id → no duplicate).
      if (res?.skill) addCreated(res.skill);
      await refresh();
      // Let the agent know it's saved so it confirms in one sentence + stops.
      releaseAgent("__saved__");
      markModalDone(toolCallId);
      setModalHandled(true);
      setModalOpen(false);
      toast.success("Skill saved to your library");
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.log("[SKILL-CREATE] save failed", { msg: e?.message });
      setErrorMsg(
        e?.message?.includes("401")
          ? "Please sign in to save this skill."
          : "Could not save the skill. Try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const sendChange = () => {
    const text = changeText.trim();
    if (!text || feedbackSent) return;
    // eslint-disable-next-line no-console
    console.log("[SKILL-CREATE] suggest change -> agent", {
      len: text.length,
      toolCallId,
    });
    // Release the waiting agent with the feedback; it re-drafts and renders a
    // fresh card. (No new chat message — this resumes the SAME turn.)
    releaseAgent(text);
    setFeedbackSent(true);
    setChangeOpen(false);
    // A fresh card (new tool call) streams in next; close this draft's modal
    // for good. The new card auto-opens its own modal.
    markModalDone(toolCallId);
    setModalHandled(true);
    setModalOpen(false);
  };

  // ── Rejected draft: stay silent; the agent re-drafts and re-calls ──
  if (rejected && !savedId) {
    return null;
  }

  // ── Loading: args still streaming (draft not complete yet) ──
  if (loadingDraft && !savedId) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full"
      >
        <div className="flex items-center gap-3 rounded-xl border border-emerald-400/15 bg-emerald-500/[0.04] px-4 py-3">
          <Loader2 className="size-4 animate-spin text-emerald-300" />
          <span className="text-[13px] text-white">Drafting your skill...</span>
        </div>
      </motion.div>
    );
  }

  // ── Saved state: a compact, expandable preview of the real skill (not just a
  //    receipt). Fires live (savedId) AND in replay (rstatus==="saved"), so the
  //    ideal sessions get it for free. Every draft field is already in scope
  //    above, so this renders the actual skill the user just built. ──
  if (savedId || rstatus === "saved") {
    const realId = savedId && savedId !== "saved" ? savedId : null;
    const previewBody = explanation || description;
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        className="w-full"
      >
        <div className="overflow-hidden rounded-2xl border border-emerald-400/25 bg-[#0b0d10] shadow-[0_8px_30px_rgba(0,0,0,0.45)]">
          {/* saved confirmation strip */}
          <div className="flex items-center gap-3 border-b border-white/[0.06] bg-gradient-to-r from-emerald-500/[0.12] via-emerald-500/[0.05] to-transparent px-4 py-3">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 ring-1 ring-inset ring-emerald-400/30">
              <Check className="size-3.5 text-emerald-300" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-bold uppercase tracking-widest text-white">
                Saved to your skills
              </p>
              <p className="truncate text-[11px] leading-tight text-white/70">
                Now in your sidebar and library.
              </p>
            </div>
            <span className="shrink-0 rounded bg-emerald-500/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-200 ring-1 ring-inset ring-emerald-400/25">
              {outputType}
            </span>
          </div>

          {/* always-visible: skill name + one-line description */}
          <div className="px-4 pt-3.5">
            <h3 className="text-[18px] font-bold leading-tight tracking-tight text-white">
              {name}
            </h3>
            {description && (
              <div className="mt-1 text-[13px] leading-[1.5] text-white/90">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={DESCRIPTION_MD_COMPONENTS}
                >
                  {description}
                </ReactMarkdown>
              </div>
            )}
          </div>

          {/* expandable detail: a short peek with a soft fade, full on expand */}
          <div
            className={cn(
              "relative overflow-hidden px-4 transition-[max-height] duration-300 ease-out",
              previewExpanded ? "max-h-[5000px]" : "max-h-[108px]",
            )}
          >
            {previewBody && (
              <article className="mt-3 text-[13.5px] leading-[1.6] text-white">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
                  {previewBody}
                </ReactMarkdown>
              </article>
            )}

            {dataSources.length > 0 && (
              <div className="mt-4">
                <span className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-white/80">
                  <Database className="size-3 text-emerald-300/80" />
                  Data sources
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {dataSources.map((d) => (
                    <span
                      key={d}
                      className="inline-flex items-center gap-2 rounded-md bg-white/[0.02] px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-widest text-white ring-1 ring-inset ring-white/[0.08]"
                    >
                      <span className="size-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
                      {prettySource(d)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {inputs.length > 0 && (
              <div className="mt-4">
                <span className="mb-2 block text-[10px] font-bold uppercase tracking-widest text-white/80">
                  Inputs each run
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {inputs.map((f) => (
                    <span
                      key={f.key}
                      className="inline-flex items-center gap-1 rounded-md bg-emerald-500/[0.06] px-2 py-1 text-[10px] font-semibold text-emerald-200 ring-1 ring-inset ring-emerald-400/20"
                    >
                      {f.label}
                      {f.required && <span className="text-emerald-400">*</span>}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="h-3" />

            {!previewExpanded && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-[#0b0d10] via-[#0b0d10]/85 to-transparent" />
            )}
          </div>

          {/* footer: expand/collapse + open in library */}
          <div className="flex items-center gap-2 border-t border-white/[0.06] bg-white/[0.01] px-4 py-2.5">
            <button
              type="button"
              onClick={() => setPreviewExpanded((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-widest text-white/85 ring-1 ring-inset ring-white/[0.10] transition-colors hover:bg-white/[0.04] hover:text-white"
            >
              <ChevronDown
                className={cn(
                  "size-3.5 transition-transform duration-200",
                  previewExpanded && "rotate-180",
                )}
              />
              {previewExpanded ? "Show less" : "Show more"}
            </button>
            {realId && (
              <a
                href={`/skills/${realId}`}
                className="ml-auto inline-flex shrink-0 items-center gap-1.5 rounded-md bg-emerald-500 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-emerald-950 transition-colors hover:bg-emerald-400"
              >
                Open in library
                <ArrowUpRight className="size-3.5" />
              </a>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  // ── Readonly / replay: static summary, no Save ──
  if (isReadonly) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full"
      >
        <div className="overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0c0c0e]">
          <div className="flex items-center gap-2.5 border-b border-white/[0.06] px-4 py-3">
            <Hammer className="size-3.5 text-emerald-300" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-white">
              Proposed skill
            </span>
          </div>
          <div className="px-4 py-3.5">
            <p className="text-[15px] font-bold text-white">{name}</p>
            {description && (
              <p className="mt-1 text-[13px] leading-snug text-white/85">
                {description}
              </p>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  const body = view === "explanation" ? explanation : instruction;

  // ── Active draft card (interactive while waiting, or read-only after the
  //    agent moved on — e.g. timeout — where Save still works). Rendered
  //    inline after the user acted, and INSIDE the review modal while the
  //    agent is waiting.
  //
  //    Split into header+content (can scroll) vs footer (Save / Suggest a
  //    change) so the modal can pin the footer to the bottom and keep it
  //    visible even when the content above is tall enough to scroll. ──
  const cardHeader = (
    <div className="relative flex items-center gap-2.5 border-b border-white/[0.06] bg-gradient-to-r from-emerald-500/[0.10] via-emerald-500/[0.04] to-transparent px-4 py-3">
      <span className="flex size-7 items-center justify-center rounded-md bg-emerald-500/15 ring-1 ring-inset ring-emerald-400/30">
        <Hammer className="size-3.5 text-emerald-300" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-bold uppercase tracking-widest text-white">
          New skill draft
        </p>
        <p className="text-[11px] leading-tight text-white/70">
          Review it. Save, or tell Xynth what to change.
        </p>
      </div>
      <span className="shrink-0 rounded bg-emerald-500/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-200 ring-1 ring-inset ring-emerald-400/25">
        {outputType}
      </span>
    </div>
  );

  const cardContent = (
        <div className="px-4 py-4">
          {/* title + one-line description */}
          <h3 className="text-[20px] font-bold leading-tight tracking-tight text-white">
            {name}
          </h3>
          {description && (
            <div className="mt-1.5 text-[13.5px] leading-[1.55] text-white/90">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={DESCRIPTION_MD_COMPONENTS}
              >
                {description}
              </ReactMarkdown>
            </div>
          )}

          {/* Explanation | Raw .md toggle — same control as the skill page */}
          <div className="relative mt-4 inline-flex items-center gap-1 rounded-md bg-white/[0.02] p-0.5 ring-1 ring-inset ring-white/[0.06]">
            {(["explanation", "raw"] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={cn(
                  "relative z-10 rounded px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors",
                  view === v ? "text-white" : "text-white/85 hover:text-white",
                )}
              >
                {view === v && (
                  <motion.div
                    layoutId={`propose-toggle-${toolCallId || "x"}`}
                    className="absolute inset-0 -z-10 rounded bg-white/[0.08] shadow-sm ring-1 ring-white/5"
                    transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                  />
                )}
                {v === "explanation" ? "Explanation" : "Raw .md"}
              </button>
            ))}
          </div>

          {/* read-only markdown body (capped height; raw .md can be long) */}
          <article className="mt-4 max-h-[440px] overflow-y-auto pr-1 text-[14px] leading-[1.6] text-white scrollbar-thin">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
              {body || "_Nothing here yet._"}
            </ReactMarkdown>
          </article>

          {/* data sources */}
          {dataSources.length > 0 && (
            <div className="mt-5 border-t border-white/[0.06] pt-4">
              <span className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-white/80">
                <Database className="size-3 text-emerald-300/80" />
                Data sources
              </span>
              <div className="flex flex-wrap gap-1.5">
                {dataSources.map((d) => (
                  <span
                    key={d}
                    className="inline-flex items-center gap-2 rounded-md bg-white/[0.02] px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-widest text-white ring-1 ring-inset ring-white/[0.08]"
                  >
                    <span className="size-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
                    {prettySource(d)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* inputs preview */}
          {inputs.length > 0 && (
            <div className="mt-4">
              <span className="mb-2 block text-[10px] font-bold uppercase tracking-widest text-white/80">
                Inputs each run
              </span>
              <div className="flex flex-wrap gap-1.5">
                {inputs.map((f) => (
                  <span
                    key={f.key}
                    className="inline-flex items-center gap-1 rounded-md bg-emerald-500/[0.06] px-2 py-1 text-[10px] font-semibold text-emerald-200 ring-1 ring-inset ring-emerald-400/20"
                  >
                    {f.label}
                    {f.required && <span className="text-emerald-400">*</span>}
                  </span>
                ))}
              </div>
            </div>
          )}

          {errorMsg && (
            <div className="mt-4 rounded-md border border-rose-400/25 bg-rose-500/[0.06] px-3 py-2 text-[12px] text-rose-200">
              {errorMsg}
            </div>
          )}
        </div>
  );

  const cardFooter = (
        <div className="shrink-0 border-t border-white/[0.06] bg-white/[0.01] px-4 py-3">
          {feedbackSent ? (
            <div className="flex items-center justify-center gap-2 rounded-md bg-emerald-500/[0.06] px-3 py-2.5 text-[11px] font-bold uppercase tracking-widest text-emerald-200">
              <Loader2 className="size-3.5 animate-spin" />
              Updating the draft below...
            </div>
          ) : changesAck ? (
            <div className="flex items-center justify-center gap-2 rounded-md bg-emerald-500/[0.06] px-3 py-2.5 text-[11px] font-bold uppercase tracking-widest text-emerald-200">
              <ArrowUpRight className="size-3.5" />
              Updated draft is below
            </div>
          ) : !user ? (
            <div className="flex items-center justify-center gap-2 rounded-md bg-white/[0.03] px-3 py-2.5 text-[11px] font-bold uppercase tracking-widest text-white/80">
              <Lock className="size-3.5" />
              Sign in to save this skill
            </div>
          ) : changeOpen ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <input
                  autoFocus
                  value={changeText}
                  onChange={(e) => setChangeText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") sendChange();
                  }}
                  maxLength={300}
                  placeholder="What should change? e.g. also pull options flow"
                  className="flex-1 rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 text-[13px] text-white outline-none transition-colors placeholder:text-white/45 hover:border-white/20 focus:border-emerald-400/50 focus:bg-white/[0.05]"
                />
                <button
                  type="button"
                  onClick={sendChange}
                  disabled={!changeText.trim()}
                  aria-label="Send change to Xynth"
                  className="inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-emerald-500 text-emerald-950 transition-colors hover:bg-emerald-400 disabled:opacity-40"
                >
                  <Send className="size-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setChangeOpen(false)}
                  aria-label="Cancel"
                  className="inline-flex size-9 shrink-0 items-center justify-center rounded-md text-white/70 ring-1 ring-inset ring-white/[0.08] transition-colors hover:text-white"
                >
                  <X className="size-4" />
                </button>
              </div>
              <p className="text-[10px] leading-snug text-white/55">
                Xynth re-drafts it and shows a fresh card. Your changes are not
                saved until you click Save.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={saving || !ready}
                  onClick={handleSave}
                  className={cn(
                    "flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2.5 text-[11px] font-bold uppercase tracking-widest transition-all",
                    saving
                      ? "cursor-wait bg-emerald-500/40 text-emerald-950"
                      : "bg-emerald-500 text-emerald-950 hover:bg-emerald-400",
                  )}
                >
                  {saving ? (
                    <>
                      <Loader2 className="size-3.5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="size-3.5" />
                      Save to my skills
                    </>
                  )}
                </button>
                {/* Suggest a change only makes sense while the agent is still
                    waiting (loading). After it moved on (timeout) we hide it —
                    the user can just type in chat. */}
                {waiting && (
                  <button
                    type="button"
                    onClick={() => setChangeOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-md px-3 py-2.5 text-[11px] font-bold uppercase tracking-widest text-white/85 ring-1 ring-inset ring-white/[0.10] transition-colors hover:bg-white/[0.04] hover:text-white"
                  >
                    <PencilLine className="size-3.5" />
                    Suggest a change
                  </button>
                )}
              </div>
              {waiting && (
                <p className="flex items-center gap-1.5 text-[10px] leading-snug text-white/80">
                  <span className="size-1.5 animate-pulse rounded-full bg-emerald-400" />
                  Xynth is waiting on you. Save it or tell it what to change.
                </p>
              )}
            </div>
          )}
        </div>
  );

  // Inline (non-modal) rendering — flows normally in chat, no fixed height,
  // so header/content/footer just stack top to bottom as before.
  const draftCard = (
    <div className="overflow-hidden rounded-2xl border border-emerald-400/20 bg-[#0b0d10] shadow-[0_8px_30px_rgba(0,0,0,0.45)]">
      {cardHeader}
      {cardContent}
      {cardFooter}
    </div>
  );

  // Modal rendering — fixed max-height with header+content scrollable and
  // the footer (Save / Suggest a change) pinned to the bottom so it is
  // ALWAYS visible, no matter how tall the draft content is.
  const modalCard = (
    <div className="flex max-h-[80dvh] flex-col overflow-hidden rounded-2xl border border-emerald-400/20 bg-[#0b0d10] shadow-[0_8px_30px_rgba(0,0,0,0.45)]">
      <div className="overflow-y-auto overscroll-contain scrollbar-thin">
        {cardHeader}
        {cardContent}
      </div>
      {cardFooter}
    </div>
  );

  // ── While the agent is waiting and the user hasn't acted: show a slim
  //    inline strip (the record in chat) + the review MODAL with the full
  //    card. Closing the modal releases the agent with __dismissed__. ──
  if (waiting && !modalHandled) {
    return (
      <>
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full"
        >
          <div className="flex items-center gap-3 rounded-xl border border-emerald-400/20 bg-emerald-500/[0.05] px-4 py-3">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-emerald-500/15 ring-1 ring-inset ring-emerald-400/30">
              <Hammer className="size-3.5 text-emerald-300" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-bold text-white">
                Skill draft ready: {name}
              </p>
              <p className="flex items-center gap-1.5 text-[11px] leading-tight text-white/80">
                <span className="size-1.5 animate-pulse rounded-full bg-emerald-400" />
                Xynth is waiting on your review.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="shrink-0 rounded-md bg-emerald-500 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-emerald-950 transition-colors hover:bg-emerald-400"
            >
              Review
            </button>
          </div>
        </motion.div>

        <DialogPrimitive.Root
          open={modalOpen}
          onOpenChange={(open) => {
            if (!open) dismissModal();
          }}
        >
          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
            <DialogPrimitive.Content
              className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-1.5rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <DialogPrimitive.Title className="sr-only">
                Review skill draft: {name}
              </DialogPrimitive.Title>
              <div className="mb-2 flex items-center justify-between px-1">
                <span className="text-[11px] font-bold uppercase tracking-widest text-white/80">
                  Review your skill draft
                </span>
                <DialogPrimitive.Close
                  aria-label="Close and dismiss draft"
                  className="inline-flex size-7 items-center justify-center rounded-md bg-white/[0.04] text-white/80 ring-1 ring-inset ring-white/[0.10] transition-colors hover:bg-white/[0.08] hover:text-white"
                >
                  <X className="size-4" />
                </DialogPrimitive.Close>
              </div>
              {modalCard}
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
      </>
    );
  }

  // ── After the user acted (saved handled above, dismissed, feedback sent) or
  //    after a timeout: the full card inline, Save still works. ──
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      className="w-full"
    >
      {draftCard}
    </motion.div>
  );
}
